import { useState } from "react";
import { MessageSquareReply, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/hooks/useApi";
import {
  getAutoresponders,
  createAutoresponder,
  updateAutoresponder,
  deleteAutoresponder,
  toggleAutoresponder,
} from "@/api/autoresponders";
import { CrudCard, ListRow, IconButton } from "./CrudCard";
import { Modal } from "./Modal";
import { showApiError } from "./helpers";
import { Field, TextInput, ChoiceSelect, ToggleRow, Toggle } from "./fields";

const MATCH_TYPES = ["contains", "exact", "startswith", "endswith", "regex"];
const EMPTY_FORM = { trigger: "", response: "", match_type: "contains", reply: true };

export const AutoResponders = ({ guildId }) => {
  const { data, loading, error, refetch } = useApi(() => getAutoresponders(guildId));
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', item?, form }
  const [saving, setSaving] = useState(false);

  const items = data?.autoreponders || [];

  const openCreate = () => setModal({ mode: "create", form: { ...EMPTY_FORM } });
  const openEdit = (item) =>
    setModal({
      mode: "edit",
      item,
      form: {
        trigger: item.trigger || "",
        response: item.response || "",
        match_type: item.match_type || "contains",
        reply: !!item.reply,
      },
    });

  const submit = async () => {
    const { form, mode, item } = modal;
    if (!form.trigger.trim() || !form.response.trim()) {
      toast.error("Trigger and response are both required.");
      return;
    }
    setSaving(true);
    try {
      if (mode === "create") {
        await createAutoresponder(guildId, { ...form, trigger: form.trigger.trim() });
        toast.success("Auto responder created.");
      } else {
        await updateAutoresponder(guildId, item.id, form);
        toast.success("Auto responder updated.");
      }
      setModal(null);
      await refetch();
    } catch (e) {
      showApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    try {
      await deleteAutoresponder(guildId, item.trigger);
      toast.success("Auto responder deleted.");
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  const toggle = async (item) => {
    try {
      await toggleAutoresponder(guildId, item.id);
      await refetch();
    } catch (e) {
      showApiError(e);
    }
  };

  return (
    <>
      <CrudCard
        icon={MessageSquareReply}
        title="Auto Responders"
        description="Reply automatically when a message matches a trigger."
        testId="crud-autoresponders"
        id="autoresponders"
        loading={loading}
        error={error}
        onRetry={refetch}
        isEmpty={items.length === 0}
        emptyText="No auto responders yet — create one to get started."
        addLabel="New responder"
        onAdd={openCreate}
      >
        <ul className="space-y-3">
          {items.map((item) => (
            <ListRow
              key={item.id}
              testId={`autoresponder-row-${item.id}`}
              actions={
                <>
                  <Toggle checked={!!item.enabled} onChange={() => toggle(item)} label={`Toggle ${item.trigger}`} testId={`autoresponder-toggle-${item.id}`} />
                  <IconButton onClick={() => openEdit(item)} label="Edit" testId={`autoresponder-edit-${item.id}`}>
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  </IconButton>
                  <IconButton onClick={() => remove(item)} label="Delete" danger testId={`autoresponder-delete-${item.id}`}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </IconButton>
                </>
              }
            >
              <p className="truncate font-mono text-sm text-paper-100">{item.trigger}</p>
              <p className="mt-0.5 truncate text-xs text-paper-500">
                {item.response} · <span className="font-mono">{item.match_type}</span>
                {item.reply ? " · replies" : ""}
              </p>
            </ListRow>
          ))}
        </ul>
      </CrudCard>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.mode === "edit" ? "Edit auto responder" : "New auto responder"} testId="autoresponder-modal">
        {modal && (
          <div className="space-y-4">
            <Field label="Trigger" hint="The word or phrase Xevon listens for.">
              <TextInput value={modal.form.trigger} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, trigger: v } }))} placeholder="hello" testId="autoresponder-form-trigger" />
            </Field>
            <Field label="Response">
              <textarea
                value={modal.form.response}
                onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, response: e.target.value } }))}
                placeholder="Hi there!"
                rows={3}
                className="w-full rounded-xl border border-white/10 bg-ink-800 px-4 py-2.5 text-sm text-paper-100 placeholder:text-paper-500/60 transition-colors duration-300 focus:border-white/30 focus:outline-none"
                data-testid="autoresponder-form-response"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Match type">
                <ChoiceSelect value={modal.form.match_type} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, match_type: v } }))} options={MATCH_TYPES} testId="autoresponder-form-match" />
              </Field>
              <div className="flex items-end pb-1">
                <ToggleRow label="Reply to message" checked={modal.form.reply} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, reply: v } }))} testId="autoresponder-form-reply" />
              </div>
            </div>
            <button
              onClick={submit}
              disabled={saving}
              data-testid="autoresponder-form-save"
              className="w-full rounded-full bg-white py-3 text-sm font-semibold text-black transition-transform duration-300 hover:scale-[1.01] disabled:opacity-50"
            >
              {saving ? "Saving…" : modal.mode === "edit" ? "Save changes" : "Create responder"}
            </button>
          </div>
        )}
      </Modal>
    </>
  );
};
